import React from 'react';
import PropTypes from 'prop-types';
import DatasetRow from './DatasetRow';

/**
 * DatasetTable
 * Main Dataset Monitor table. Columns are locked by the design (spec 12):
 * Dataset | New data added to DB | Current data changed in DB |
 * Coverage <hours>h | Flow | Alert email | Notification email | Status.
 *
 * `hours` is the same reporting window the container sent to
 * GET /dataset-monitor/datasets and is threaded straight into the "Coverage"
 * header - the coverage numbers underneath change when the container's
 * window selector changes, so a hardcoded "24h" would mislabel the column
 * the moment an admin picks a different window.
 *
 * Rows group under section headers by `group`, matching how the mockup's
 * own DATASETS array is organised. The documented index payload (design
 * spec 10) sends `group` as a plain label string with no accompanying sort
 * key, so section order here mirrors DatasetMonitorGroup's own documented
 * declaration order (Complaints, Violations, Orders, Permits & Equipment,
 * Certificates, Landmarks, Property Records) - the same
 * known-order-then-alphabetical-fallback technique PerQueueTable already
 * uses for queue names, so a group the frontend does not recognise still
 * renders (at the end) instead of being silently dropped.
 */

export const KNOWN_GROUP_ORDER = [
	'Complaints',
	'Violations',
	'Orders',
	'Permits & Equipment',
	'Certificates',
	'Landmarks',
	'Property Records',
];

const UNGROUPED_LABEL = 'Other';

/**
 * Known groups first (in DatasetMonitorGroup's declared order), then any
 * unrecognised group alphabetically. Exported so the container's group
 * filter dropdown lists options in the same order as the table's own
 * section headers, without a second hardcoded copy of the order.
 */
export const sortGroupNames = (groups) => {
	const known = KNOWN_GROUP_ORDER.filter((group) => groups.includes(group));
	const unknown = groups
		.filter((group) => !KNOWN_GROUP_ORDER.includes(group))
		.sort((a, b) => a.localeCompare(b));
	return [...known, ...unknown];
};

const groupDatasets = (datasets) => {
	const byGroup = new Map();
	datasets.forEach((dataset) => {
		const group = dataset.group || UNGROUPED_LABEL;
		if (!byGroup.has(group)) {
			byGroup.set(group, []);
		}
		byGroup.get(group).push(dataset);
	});

	return sortGroupNames(Array.from(byGroup.keys())).map((group) => ({
		group,
		datasets: byGroup.get(group),
	}));
};

/** Columns rendered in <thead>; the group header row must span all of them. */
const TABLE_COLUMN_COUNT = 9;

const DatasetTable = ({ datasets, onSelectDataset, hours }) => {
	if (!Array.isArray(datasets) || datasets.length === 0) {
		return (
			<p className="text-muted dataset-table-empty">
				No datasets match the current filters.
			</p>
		);
	}

	const sections = groupDatasets(datasets);

	return (
		<div className="table-responsive dataset-monitor-table">
			<table className="table table-sm table-hover">
				<thead className="table-light">
					<tr>
						<th>Dataset</th>
						<th>New data added to DB</th>
						<th>Current data changed in DB</th>
						<th>Coverage {hours}h</th>
						<th>Flow</th>
						<th>Alert email</th>
						<th>Notification email</th>
						<th>Reminder email</th>
						<th>Status</th>
					</tr>
				</thead>
				<tbody>
					{sections.map(({ group, datasets: sectionDatasets }) => (
						<React.Fragment key={group}>
							<tr className="dataset-group-header">
								<td colSpan={TABLE_COLUMN_COUNT}>{group}</td>
							</tr>
							{sectionDatasets.map((dataset) => (
								<DatasetRow
									key={dataset.key}
									dataset={dataset}
									onSelect={onSelectDataset}
								/>
							))}
						</React.Fragment>
					))}
				</tbody>
			</table>
		</div>
	);
};

DatasetTable.propTypes = {
	datasets: PropTypes.arrayOf(PropTypes.object),
	onSelectDataset: PropTypes.func.isRequired,
	hours: PropTypes.number,
};

DatasetTable.defaultProps = {
	datasets: [],
	hours: 24,
};

export default DatasetTable;
